import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { push, replace } from 'redux-first-history';
import * as API from '../api/index.js';
import { mapUserForAutocomplete } from '../utils/teamHelper.js';

export const fetchAllTeams = createAsyncThunk('teams/fetchAll', async (_, { rejectWithValue }) => {
    try {
        const payload = await API.team.getAll();
        return payload;
    } catch (error) {
        notification.error({ message: 'Error', description: error.message || 'Server error' });
        return rejectWithValue(error.message);
    }
});

export const fetchTeams = createAsyncThunk(
    'teams/fetchList',
    async ({ page, query }, { getState, rejectWithValue }) => {
        try {
            const perPage = getState().general.settings.perPage;
            const result = await API.team.get(page, perPage, query);

            const currentList = getState().team.teams.list;

            const { maxPageNumber, teams } = result;
            return {
                maxPageNumber,
                list: page ? [...currentList, ...teams] : teams,
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    },
);

export const fetchCreateTeam = createAsyncThunk('teams/create', async ({ data }, { dispatch, rejectWithValue }) => {
    try {
        const team = await API.team.create(data);
        dispatch({ type: 'general/toggleModal', payload: { modal: 'team', isOpen: false } });
        dispatch(push(`teams/team/${team.id}`));
        return team;
    } catch (error) {
        notification.error({ message: 'Error', description: 'Server error' });
        return rejectWithValue(error.message);
    }
});

export const fetchUpdateTeam = createAsyncThunk('teams/update', async ({ data, id }, { dispatch, rejectWithValue }) => {
    try {
        await API.team.update(data, id);
        notification.info({ message: 'Success', description: 'Team was updated' });
        dispatch({ type: 'general/toggleModal', payload: { modal: 'teamEdit', isOpen: false } });
        return { data, id };
    } catch (error) {
        notification.error({ message: 'Error', description: error.message });
        return rejectWithValue(error.message);
    }
});

export const fetchDeleteTeam = createAsyncThunk('teams/delete', async ({ id }, { rejectWithValue }) => {
    try {
        await API.team.delete(id);
        notification.info({ message: 'Success', description: 'Team was deleted' });
        return id;
    } catch (error) {
        notification.error({ message: 'Error', description: error.message });
        return rejectWithValue(error.message);
    }
});

export const fetchTeam = createAsyncThunk('teams/fetchById', async ({ id }, { dispatch, rejectWithValue }) => {
    try {
        const team = await API.team.getById(id);
        if (!team) {
            dispatch(replace('/404'));
            return rejectWithValue('Team not found');
        }
        return team;
    } catch (error) {
        dispatch(replace('/404'));
        notification.error({ message: 'Error', description: error.message });
        return rejectWithValue(error.message);
    }
});

export const fetchTeamEmployees = createAsyncThunk(
    'teams/fetchEmployees',
    async ({ teamId, page, query }, { getState, rejectWithValue }) => {
        try {
            const perPage = getState().general.settings.perPage;
            const { maxPageNumber, users } = await API.team.getTeamEmployees(teamId, page, perPage, query);
            const currentList = getState().team.users.list;
            return {
                maxPageNumber,
                list: page ? [...currentList, ...users] : users,
            };
        } catch (error) {
            notification.error({ message: 'Error', description: error.message });
            return rejectWithValue(error.message);
        }
    },
);

export const excludeEmployee = createAsyncThunk(
    'teams/excludeEmployee',
    async ({ employeeId, autoApprove }, { getState, rejectWithValue }) => {
        try {
            const teamId = getState().team.team.id;
            await API.team.excludeEmployee(teamId, employeeId, autoApprove);
            notification.info({ message: 'Success', description: 'Employee was removed from team' });
            return employeeId;
        } catch (error) {
            notification.error({ message: 'Error', description: error.message });
            return rejectWithValue(error.message);
        }
    },
);

export const updateEmployee = createAsyncThunk('teams/updateEmployee', async ({ payload }, { rejectWithValue }) => {
    const { teamId, employeeId, weight, isApprover, autoApprove } = payload;
    try {
        await API.team.updateEmployee(teamId, employeeId, { weight, isApprover, autoApprove });
        notification.info({ message: 'Success', description: 'Approval order was changed' });
        return payload;
    } catch (error) {
        notification.error({ message: 'Error', description: error.message });
        return rejectWithValue(error.message);
    }
});

export const fetchNotAttachedEmployee = createAsyncThunk(
    'teams/fetchNonAttached',
    async (_, { getState, rejectWithValue }) => {
        try {
            const teamId = getState().team.team.id;
            const result = await API.team.getNonIncludedEmployees(teamId);
            return mapUserForAutocomplete(result);
        } catch (error) {
            notification.error({ message: 'Error', description: error.message });
            return rejectWithValue(error.message);
        }
    },
);

export const attachEmployee = createAsyncThunk(
    'teams/attachEmployee',
    async ({ employeeIds }, { getState, dispatch, rejectWithValue }) => {
        const teamId = getState().team.team.id;
        try {
            const users = [];
            for (let userId of employeeIds) {
                users.push(await API.team.attachEmployee(teamId, userId));
            }
            notification.info({ message: 'Success', description: 'Employee was added to team' });
            dispatch({ type: 'general/toggleModal', payload: { modal: 'addToTeam', isOpen: false } });
            return users;
        } catch (error) {
            notification.error({ message: 'Error', description: error.message });
            return rejectWithValue(error.message);
        }
    },
);
